/**
 * Basketball Data Service
 * Unified data fetching for all basketball sports: NBA, WNBA, MBB, WBB
 */

import apiClient from './apiClient';

// API Base URLs
const SLAM_API_BASE = 'https://slam-all-python-359065791766.us-central1.run.app';
const ESPN_API_BASE = 'https://site.api.espn.com/apis/site/v2/sports/basketball';

// Sport-specific configurations
const SPORT_CONFIG = {
  NBA: {
    espnLeague: 'nba',
    apiPath: 'NBA',
    teamEndpoint: '/nba/teams',
    playerEndpoint: '/nba/players',
    statsEndpoint: '/nba/stats',
    // ESPN endpoints for live data
    espnTeams: `${ESPN_API_BASE}/nba/teams`,
    espnScoreboard: `${ESPN_API_BASE}/nba/scoreboard`,
  },
  WNBA: {
    espnLeague: 'wnba',
    apiPath: 'WNBA',
    teamEndpoint: '/wnba/teams',
    playerEndpoint: '/wnba/players',
    statsEndpoint: '/wnba/stats',
    espnTeams: `${ESPN_API_BASE}/wnba/teams`,
    espnScoreboard: `${ESPN_API_BASE}/wnba/scoreboard`,
  },
  MBB: {
    espnLeague: 'mens-college-basketball',
    apiPath: 'MBB',
    teamEndpoint: `${SLAM_API_BASE}/MBB/team-stats-mia/`,
    playerEndpoint: `${SLAM_API_BASE}/MBB/official-roasters/search`,
    statsEndpoint: `${SLAM_API_BASE}/MBB/player-stats`,
  },
  WBB: {
    espnLeague: 'womens-college-basketball',
    apiPath: 'WBB',
    teamEndpoint: `${SLAM_API_BASE}/WBB/team-stats-mia/`,
    playerEndpoint: `${SLAM_API_BASE}/WBB/official-roasters/search`,
    statsEndpoint: `${SLAM_API_BASE}/WBB/player-stats`,
  },
  EURO: {
    espnLeague: 'euroleague',
    apiPath: 'EURO',
    teamEndpoint: '/euro/teams',
    playerEndpoint: '/euro/players',
    statsEndpoint: '/euro/stats',
  },
};

// NBA Teams (30 teams)
export const NBA_TEAMS = [
  { id: 'ATL', name: 'Atlanta Hawks', conference: 'Eastern', division: 'Southeast' },
  { id: 'BOS', name: 'Boston Celtics', conference: 'Eastern', division: 'Atlantic' },
  { id: 'BKN', name: 'Brooklyn Nets', conference: 'Eastern', division: 'Atlantic' },
  { id: 'CHA', name: 'Charlotte Hornets', conference: 'Eastern', division: 'Southeast' },
  { id: 'CHI', name: 'Chicago Bulls', conference: 'Eastern', division: 'Central' },
  { id: 'CLE', name: 'Cleveland Cavaliers', conference: 'Eastern', division: 'Central' },
  { id: 'DAL', name: 'Dallas Mavericks', conference: 'Western', division: 'Southwest' },
  { id: 'DEN', name: 'Denver Nuggets', conference: 'Western', division: 'Northwest' },
  { id: 'DET', name: 'Detroit Pistons', conference: 'Eastern', division: 'Central' },
  { id: 'GSW', name: 'Golden State Warriors', conference: 'Western', division: 'Pacific' },
  { id: 'HOU', name: 'Houston Rockets', conference: 'Western', division: 'Southwest' },
  { id: 'IND', name: 'Indiana Pacers', conference: 'Eastern', division: 'Central' },
  { id: 'LAC', name: 'LA Clippers', conference: 'Western', division: 'Pacific' },
  { id: 'LAL', name: 'Los Angeles Lakers', conference: 'Western', division: 'Pacific' },
  { id: 'MEM', name: 'Memphis Grizzlies', conference: 'Western', division: 'Southwest' },
  { id: 'MIA', name: 'Miami Heat', conference: 'Eastern', division: 'Southeast' },
  { id: 'MIL', name: 'Milwaukee Bucks', conference: 'Eastern', division: 'Central' },
  { id: 'MIN', name: 'Minnesota Timberwolves', conference: 'Western', division: 'Northwest' },
  { id: 'NOP', name: 'New Orleans Pelicans', conference: 'Western', division: 'Southwest' },
  { id: 'NYK', name: 'New York Knicks', conference: 'Eastern', division: 'Atlantic' },
  { id: 'OKC', name: 'Oklahoma City Thunder', conference: 'Western', division: 'Northwest' },
  { id: 'ORL', name: 'Orlando Magic', conference: 'Eastern', division: 'Southeast' },
  { id: 'PHI', name: 'Philadelphia 76ers', conference: 'Eastern', division: 'Atlantic' },
  { id: 'PHX', name: 'Phoenix Suns', conference: 'Western', division: 'Pacific' },
  { id: 'POR', name: 'Portland Trail Blazers', conference: 'Western', division: 'Northwest' },
  { id: 'SAC', name: 'Sacramento Kings', conference: 'Western', division: 'Pacific' },
  { id: 'SAS', name: 'San Antonio Spurs', conference: 'Western', division: 'Southwest' },
  { id: 'TOR', name: 'Toronto Raptors', conference: 'Eastern', division: 'Atlantic' },
  { id: 'UTA', name: 'Utah Jazz', conference: 'Western', division: 'Northwest' },
  { id: 'WAS', name: 'Washington Wizards', conference: 'Eastern', division: 'Southeast' },
];

// =============================================================================
// NBA ROSTERS (Hardcoded for reliable data loading)
// =============================================================================

const NBA_ROSTERS = {
  'Atlanta Hawks': [
    { id: 'trae-young', firstName: 'Trae', lastName: 'Young', position: 'PG', number: 11, height: "6'1\"", weight: 180, ppg: 25.7, apg: 10.8, rpg: 2.8, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4277905.png' },
    { id: 'dejounte-murray', firstName: 'Dejounte', lastName: 'Murray', position: 'SG', number: 5, height: "6'4\"", weight: 180, ppg: 20.5, apg: 6.1, rpg: 5.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3907497.png' },
    { id: 'bogdan-bogdanovic', firstName: 'Bogdan', lastName: 'Bogdanovic', position: 'SG', number: 13, height: "6'6\"", weight: 205, ppg: 14.2, apg: 3.5, rpg: 3.0, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3037789.png' },
    { id: 'deandre-hunter', firstName: "De'Andre", lastName: 'Hunter', position: 'SF', number: 12, height: "6'8\"", weight: 225, ppg: 13.4, apg: 1.6, rpg: 4.0, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4065732.png' },
    { id: 'jalen-johnson', firstName: 'Jalen', lastName: 'Johnson', position: 'PF', number: 1, height: "6'9\"", weight: 220, ppg: 16.0, apg: 3.6, rpg: 8.7, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4701230.png' },
    { id: 'clint-capela', firstName: 'Clint', lastName: 'Capela', position: 'C', number: 15, height: "6'10\"", weight: 240, ppg: 11.5, apg: 1.2, rpg: 10.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3102529.png' },
    { id: 'onyeka-okongwu', firstName: 'Onyeka', lastName: 'Okongwu', position: 'C', number: 17, height: "6'8\"", weight: 235, ppg: 9.8, apg: 1.1, rpg: 6.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4431680.png' },
    { id: 'saddiq-bey', firstName: 'Saddiq', lastName: 'Bey', position: 'SF', number: 41, height: "6'7\"", weight: 215, ppg: 10.1, apg: 1.8, rpg: 4.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4397136.png' },
    { id: 'zaccharie-risacher', firstName: 'Zaccharie', lastName: 'Risacher', position: 'SF', number: 10, height: "6'8\"", weight: 200, ppg: 11.2, apg: 1.4, rpg: 3.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/5211175.png' },
  ],
  'Boston Celtics': [
    { id: 'jayson-tatum', firstName: 'Jayson', lastName: 'Tatum', position: 'SF', number: 0, height: "6'8\"", weight: 210, ppg: 26.9, apg: 4.9, rpg: 8.1, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4065648.png' },
    { id: 'jaylen-brown', firstName: 'Jaylen', lastName: 'Brown', position: 'SG', number: 7, height: "6'6\"", weight: 223, ppg: 23.0, apg: 3.6, rpg: 5.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3917376.png' },
    { id: 'derrick-white', firstName: 'Derrick', lastName: 'White', position: 'PG', number: 9, height: "6'4\"", weight: 190, ppg: 15.2, apg: 5.2, rpg: 4.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3078576.png' },
    { id: 'jrue-holiday', firstName: 'Jrue', lastName: 'Holiday', position: 'PG', number: 4, height: "6'4\"", weight: 205, ppg: 12.5, apg: 4.8, rpg: 5.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3995.png' },
    { id: 'kristaps-porzingis', firstName: 'Kristaps', lastName: 'Porzingis', position: 'C', number: 8, height: "7'3\"", weight: 240, ppg: 20.1, apg: 1.9, rpg: 7.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3102531.png' },
    { id: 'al-horford', firstName: 'Al', lastName: 'Horford', position: 'C', number: 42, height: "6'9\"", weight: 240, ppg: 8.6, apg: 3.8, rpg: 6.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3213.png' },
    { id: 'payton-pritchard', firstName: 'Payton', lastName: 'Pritchard', position: 'PG', number: 11, height: "6'1\"", weight: 195, ppg: 9.5, apg: 2.4, rpg: 2.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4066354.png' },
  ],
  'Brooklyn Nets': [
    { id: 'mikal-bridges', firstName: 'Mikal', lastName: 'Bridges', position: 'SF', number: 1, height: "6'6\"", weight: 209, ppg: 19.6, apg: 3.3, rpg: 4.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3147657.png' },
    { id: 'cameron-johnson', firstName: 'Cameron', lastName: 'Johnson', position: 'SF', number: 2, height: "6'8\"", weight: 210, ppg: 13.5, apg: 1.7, rpg: 4.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3138196.png' },
    { id: 'nic-claxton', firstName: 'Nic', lastName: 'Claxton', position: 'C', number: 33, height: "6'11\"", weight: 215, ppg: 12.1, apg: 2.0, rpg: 8.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4278067.png' },
    { id: 'spencer-dinwiddie', firstName: 'Spencer', lastName: 'Dinwiddie', position: 'PG', number: 26, height: "6'5\"", weight: 215, ppg: 14.3, apg: 5.2, rpg: 3.1, image: 'https://a.espncdn.com/i/headshots/nba/players/full/2580782.png' },
    { id: 'cam-thomas', firstName: 'Cam', lastName: 'Thomas', position: 'SG', number: 24, height: "6'3\"", weight: 210, ppg: 22.5, apg: 3.4, rpg: 3.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4432174.png' },
  ],
  'Charlotte Hornets': [
    { id: 'lamelo-ball', firstName: 'LaMelo', lastName: 'Ball', position: 'PG', number: 1, height: "6'7\"", weight: 180, ppg: 23.9, apg: 8.0, rpg: 6.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4432816.png' },
    { id: 'brandon-miller', firstName: 'Brandon', lastName: 'Miller', position: 'SF', number: 24, height: "6'9\"", weight: 200, ppg: 17.3, apg: 2.4, rpg: 4.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4433287.png' },
    { id: 'miles-bridges', firstName: 'Miles', lastName: 'Bridges', position: 'PF', number: 0, height: "6'6\"", weight: 225, ppg: 21.0, apg: 3.3, rpg: 7.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4066383.png' },
    { id: 'mark-williams', firstName: 'Mark', lastName: 'Williams', position: 'C', number: 5, height: "7'0\"", weight: 242, ppg: 9.7, apg: 1.1, rpg: 7.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4701232.png' },
    { id: 'terry-rozier', firstName: 'Terry', lastName: 'Rozier', position: 'SG', number: 3, height: "6'1\"", weight: 190, ppg: 20.4, apg: 5.0, rpg: 4.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3074752.png' },
  ],
  'Chicago Bulls': [
    { id: 'demar-derozan', firstName: 'DeMar', lastName: 'DeRozan', position: 'SF', number: 11, height: "6'6\"", weight: 220, ppg: 24.0, apg: 5.3, rpg: 4.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3978.png' },
    { id: 'zach-lavine', firstName: 'Zach', lastName: 'LaVine', position: 'SG', number: 8, height: "6'5\"", weight: 200, ppg: 19.5, apg: 3.9, rpg: 4.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3064440.png' },
    { id: 'nikola-vucevic', firstName: 'Nikola', lastName: 'Vucevic', position: 'C', number: 9, height: "6'11\"", weight: 260, ppg: 18.0, apg: 3.6, rpg: 10.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/6478.png' },
    { id: 'patrick-williams', firstName: 'Patrick', lastName: 'Williams', position: 'PF', number: 44, height: "6'7\"", weight: 215, ppg: 10.1, apg: 1.8, rpg: 3.9, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4431687.png' },
    { id: 'coby-white', firstName: 'Coby', lastName: 'White', position: 'PG', number: 0, height: "6'5\"", weight: 195, ppg: 12.7, apg: 4.5, rpg: 3.0, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4395651.png' },
  ],
  'Cleveland Cavaliers': [
    { id: 'donovan-mitchell', firstName: 'Donovan', lastName: 'Mitchell', position: 'SG', number: 45, height: "6'1\"", weight: 215, ppg: 28.3, apg: 5.1, rpg: 4.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3908809.png' },
    { id: 'darius-garland', firstName: 'Darius', lastName: 'Garland', position: 'PG', number: 10, height: "6'1\"", weight: 192, ppg: 18.0, apg: 6.2, rpg: 2.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4396907.png' },
    { id: 'evan-mobley', firstName: 'Evan', lastName: 'Mobley', position: 'PF', number: 4, height: "7'0\"", weight: 215, ppg: 15.7, apg: 3.2, rpg: 9.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4432158.png' },
    { id: 'jarrett-allen', firstName: 'Jarrett', lastName: 'Allen', position: 'C', number: 31, height: "6'11\"", weight: 243, ppg: 16.5, apg: 1.7, rpg: 10.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4066328.png' },
    { id: 'max-strus', firstName: 'Max', lastName: 'Strus', position: 'SG', number: 1, height: "6'5\"", weight: 215, ppg: 12.8, apg: 2.8, rpg: 4.1, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4065778.png' },
  ],
  'Dallas Mavericks': [
    { id: 'luka-doncic', firstName: 'Luka', lastName: 'Doncic', position: 'PG', number: 77, height: "6'7\"", weight: 230, ppg: 33.9, apg: 9.8, rpg: 9.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3945274.png' },
    { id: 'kyrie-irving', firstName: 'Kyrie', lastName: 'Irving', position: 'PG', number: 11, height: "6'2\"", weight: 195, ppg: 25.6, apg: 5.2, rpg: 5.0, image: 'https://a.espncdn.com/i/headshots/nba/players/full/6442.png' },
    { id: 'pj-washington', firstName: 'P.J.', lastName: 'Washington', position: 'PF', number: 25, height: "6'7\"", weight: 230, ppg: 13.4, apg: 2.1, rpg: 5.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4278078.png' },
    { id: 'daniel-gafford', firstName: 'Daniel', lastName: 'Gafford', position: 'C', number: 21, height: "6'10\"", weight: 234, ppg: 11.2, apg: 1.0, rpg: 6.9, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4278049.png' },
    { id: 'dereck-lively', firstName: 'Dereck', lastName: 'Lively II', position: 'C', number: 2, height: "7'1\"", weight: 230, ppg: 8.8, apg: 1.2, rpg: 6.9, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4683688.png' },
  ],
  'Denver Nuggets': [
    { id: 'nikola-jokic', firstName: 'Nikola', lastName: 'Jokic', position: 'C', number: 15, height: "6'11\"", weight: 284, ppg: 26.4, apg: 9.0, rpg: 12.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3112335.png' },
    { id: 'jamal-murray', firstName: 'Jamal', lastName: 'Murray', position: 'PG', number: 27, height: "6'4\"", weight: 215, ppg: 21.2, apg: 6.5, rpg: 4.1, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3936299.png' },
    { id: 'michael-porter', firstName: 'Michael', lastName: 'Porter Jr.', position: 'SF', number: 1, height: "6'10\"", weight: 218, ppg: 16.7, apg: 1.5, rpg: 7.0, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4278104.png' },
    { id: 'aaron-gordon', firstName: 'Aaron', lastName: 'Gordon', position: 'PF', number: 50, height: "6'8\"", weight: 235, ppg: 13.9, apg: 3.5, rpg: 6.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3064290.png' },
    { id: 'kentavious-caldwell-pope', firstName: 'Kentavious', lastName: 'Caldwell-Pope', position: 'SG', number: 5, height: "6'5\"", weight: 204, ppg: 10.1, apg: 2.4, rpg: 2.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/2581018.png' },
  ],
  'Detroit Pistons': [
    { id: 'cade-cunningham', firstName: 'Cade', lastName: 'Cunningham', position: 'PG', number: 2, height: "6'6\"", weight: 220, ppg: 22.7, apg: 7.5, rpg: 4.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4432166.png' },
    { id: 'jaden-ivey', firstName: 'Jaden', lastName: 'Ivey', position: 'SG', number: 23, height: "6'4\"", weight: 195, ppg: 16.2, apg: 5.2, rpg: 3.9, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4433218.png' },
    { id: 'ausar-thompson', firstName: 'Ausar', lastName: 'Thompson', position: 'SF', number: 9, height: "6'6\"", weight: 201, ppg: 8.8, apg: 2.4, rpg: 6.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4684742.png' },
    { id: 'jalen-duren', firstName: 'Jalen', lastName: 'Duren', position: 'C', number: 0, height: "6'11\"", weight: 250, ppg: 13.0, apg: 1.3, rpg: 11.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4433621.png' },
    { id: 'marcus-sasser', firstName: 'Marcus', lastName: 'Sasser', position: 'PG', number: 25, height: "6'1\"", weight: 175, ppg: 6.4, apg: 2.8, rpg: 1.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4432107.png' },
  ],
  'Golden State Warriors': [
    { id: 'stephen-curry', firstName: 'Stephen', lastName: 'Curry', position: 'PG', number: 30, height: "6'2\"", weight: 185, ppg: 26.4, apg: 5.1, rpg: 4.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3975.png' },
    { id: 'klay-thompson', firstName: 'Klay', lastName: 'Thompson', position: 'SG', number: 11, height: "6'6\"", weight: 220, ppg: 17.9, apg: 2.3, rpg: 3.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/6475.png' },
    { id: 'andrew-wiggins', firstName: 'Andrew', lastName: 'Wiggins', position: 'SF', number: 22, height: "6'7\"", weight: 197, ppg: 13.2, apg: 1.7, rpg: 4.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3059319.png' },
    { id: 'draymond-green', firstName: 'Draymond', lastName: 'Green', position: 'PF', number: 23, height: "6'6\"", weight: 230, ppg: 8.6, apg: 6.0, rpg: 7.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/6589.png' },
    { id: 'jonathan-kuminga', firstName: 'Jonathan', lastName: 'Kuminga', position: 'SF', number: '00', height: "6'8\"", weight: 225, ppg: 9.9, apg: 1.9, rpg: 3.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4433247.png' },
  ],
  'Houston Rockets': [
    { id: 'jalen-green', firstName: 'Jalen', lastName: 'Green', position: 'SG', number: 4, height: "6'4\"", weight: 186, ppg: 19.6, apg: 3.7, rpg: 5.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4437244.png' },
    { id: 'alperen-sengun', firstName: 'Alperen', lastName: 'Sengun', position: 'C', number: 28, height: "6'9\"", weight: 240, ppg: 21.1, apg: 5.0, rpg: 9.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4871144.png' },
    { id: 'fred-vanvleet', firstName: 'Fred', lastName: 'VanVleet', position: 'PG', number: 5, height: "6'1\"", weight: 197, ppg: 14.3, apg: 5.9, rpg: 3.8, image: 'https://a.espncdn.com/i/headshots/nba/players/full/2991230.png' },
    { id: 'dillon-brooks', firstName: 'Dillon', lastName: 'Brooks', position: 'SF', number: 9, height: "6'6\"", weight: 225, ppg: 12.7, apg: 2.2, rpg: 3.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3155526.png' },
    { id: 'jabari-smith', firstName: 'Jabari', lastName: 'Smith Jr.', position: 'PF', number: 10, height: "6'10\"", weight: 220, ppg: 12.8, apg: 1.6, rpg: 6.8, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4432639.png' },
  ],
  'Indiana Pacers': [
    { id: 'tyrese-haliburton', firstName: 'Tyrese', lastName: 'Haliburton', position: 'PG', number: 0, height: "6'5\"", weight: 185, ppg: 20.1, apg: 10.9, rpg: 3.9, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4396993.png' },
    { id: 'pascal-siakam', firstName: 'Pascal', lastName: 'Siakam', position: 'PF', number: 43, height: "6'9\"", weight: 230, ppg: 21.3, apg: 3.7, rpg: 7.8, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3149673.png' },
    { id: 'myles-turner', firstName: 'Myles', lastName: 'Turner', position: 'C', number: 33, height: "6'11\"", weight: 250, ppg: 17.1, apg: 1.4, rpg: 6.9, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3133628.png' },
    { id: 'benedict-mathurin', firstName: 'Benedict', lastName: 'Mathurin', position: 'SG', number: '00', height: "6'6\"", weight: 210, ppg: 14.5, apg: 1.7, rpg: 4.1, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4683634.png' },
    { id: 'aaron-nesmith', firstName: 'Aaron', lastName: 'Nesmith', position: 'SF', number: 23, height: "6'5\"", weight: 215, ppg: 10.4, apg: 1.6, rpg: 3.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4396909.png' },
  ],
  'LA Clippers': [
    { id: 'kawhi-leonard', firstName: 'Kawhi', lastName: 'Leonard', position: 'SF', number: 2, height: "6'7\"", weight: 225, ppg: 23.7, apg: 3.6, rpg: 6.1, image: 'https://a.espncdn.com/i/headshots/nba/players/full/6450.png' },
    { id: 'paul-george', firstName: 'Paul', lastName: 'George', position: 'SF', number: 13, height: "6'8\"", weight: 220, ppg: 22.6, apg: 5.2, rpg: 5.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4251.png' },
    { id: 'james-harden', firstName: 'James', lastName: 'Harden', position: 'PG', number: 1, height: "6'5\"", weight: 220, ppg: 16.6, apg: 8.5, rpg: 5.1, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3992.png' },
    { id: 'ivica-zubac', firstName: 'Ivica', lastName: 'Zubac', position: 'C', number: 40, height: "7'0\"", weight: 240, ppg: 11.7, apg: 1.3, rpg: 9.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4017837.png' },
    { id: 'norman-powell', firstName: 'Norman', lastName: 'Powell', position: 'SG', number: 24, height: "6'3\"", weight: 215, ppg: 13.9, apg: 1.9, rpg: 2.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/2595516.png' },
  ],
  'Los Angeles Lakers': [
    { id: 'lebron-james', firstName: 'LeBron', lastName: 'James', position: 'SF', number: 23, height: "6'9\"", weight: 250, ppg: 25.7, apg: 8.3, rpg: 7.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/1966.png' },
    { id: 'anthony-davis', firstName: 'Anthony', lastName: 'Davis', position: 'PF', number: 3, height: "6'10\"", weight: 253, ppg: 24.7, apg: 3.5, rpg: 12.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/6583.png' },
    { id: 'austin-reaves', firstName: 'Austin', lastName: 'Reaves', position: 'SG', number: 15, height: "6'5\"", weight: 197, ppg: 15.9, apg: 5.5, rpg: 4.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4066457.png' },
    { id: 'dangelo-russell', firstName: "D'Angelo", lastName: 'Russell', position: 'PG', number: 1, height: "6'4\"", weight: 193, ppg: 18.0, apg: 6.3, rpg: 3.1, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3136776.png' },
    { id: 'rui-hachimura', firstName: 'Rui', lastName: 'Hachimura', position: 'PF', number: 28, height: "6'8\"", weight: 230, ppg: 13.6, apg: 1.2, rpg: 4.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4066648.png' },
  ],
  'Memphis Grizzlies': [
    { id: 'ja-morant', firstName: 'Ja', lastName: 'Morant', position: 'PG', number: 12, height: "6'3\"", weight: 174, ppg: 25.1, apg: 8.1, rpg: 5.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4279888.png' },
    { id: 'jaren-jackson', firstName: 'Jaren', lastName: 'Jackson Jr.', position: 'PF', number: 13, height: "6'10\"", weight: 242, ppg: 22.5, apg: 2.1, rpg: 5.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4277961.png' },
    { id: 'desmond-bane', firstName: 'Desmond', lastName: 'Bane', position: 'SG', number: 22, height: "6'5\"", weight: 215, ppg: 23.7, apg: 5.1, rpg: 5.1, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4066320.png' },
    { id: 'marcus-smart', firstName: 'Marcus', lastName: 'Smart', position: 'PG', number: 36, height: "6'3\"", weight: 220, ppg: 14.2, apg: 5.5, rpg: 3.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/2990992.png' },
    { id: 'steven-adams', firstName: 'Steven', lastName: 'Adams', position: 'C', number: 4, height: "6'11\"", weight: 265, ppg: 8.6, apg: 2.3, rpg: 11.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/2991235.png' },
  ],
  'Miami Heat': [
    { id: 'jimmy-butler', firstName: 'Jimmy', lastName: 'Butler', position: 'SF', number: 22, height: "6'7\"", weight: 230, ppg: 20.8, apg: 5.0, rpg: 5.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/6430.png' },
    { id: 'bam-adebayo', firstName: 'Bam', lastName: 'Adebayo', position: 'C', number: 13, height: "6'9\"", weight: 255, ppg: 19.3, apg: 3.9, rpg: 10.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4066261.png' },
    { id: 'tyler-herro', firstName: 'Tyler', lastName: 'Herro', position: 'SG', number: 14, height: "6'5\"", weight: 195, ppg: 20.8, apg: 4.5, rpg: 5.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4395725.png' },
    { id: 'terry-rozier', firstName: 'Terry', lastName: 'Rozier', position: 'PG', number: 3, height: "6'1\"", weight: 190, ppg: 17.5, apg: 4.2, rpg: 3.8, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3074752.png' },
    { id: 'jaime-jaquez', firstName: 'Jaime', lastName: 'Jaquez Jr.', position: 'SF', number: 11, height: "6'7\"", weight: 225, ppg: 11.9, apg: 2.6, rpg: 3.8, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4432848.png' },
  ],
  'Milwaukee Bucks': [
    { id: 'giannis-antetokounmpo', firstName: 'Giannis', lastName: 'Antetokounmpo', position: 'PF', number: 34, height: "6'11\"", weight: 243, ppg: 30.4, apg: 6.5, rpg: 11.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3032977.png' },
    { id: 'damian-lillard', firstName: 'Damian', lastName: 'Lillard', position: 'PG', number: 0, height: "6'2\"", weight: 195, ppg: 24.3, apg: 7.0, rpg: 4.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/6606.png' },
    { id: 'khris-middleton', firstName: 'Khris', lastName: 'Middleton', position: 'SF', number: 22, height: "6'7\"", weight: 222, ppg: 15.1, apg: 4.7, rpg: 4.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/6609.png' },
    { id: 'brook-lopez', firstName: 'Brook', lastName: 'Lopez', position: 'C', number: 11, height: "7'0\"", weight: 282, ppg: 12.5, apg: 1.6, rpg: 5.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3448.png' },
    { id: 'bobby-portis', firstName: 'Bobby', lastName: 'Portis', position: 'PF', number: 9, height: "6'10\"", weight: 250, ppg: 13.8, apg: 1.6, rpg: 7.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3064482.png' },
  ],
  'Minnesota Timberwolves': [
    { id: 'anthony-edwards', firstName: 'Anthony', lastName: 'Edwards', position: 'SG', number: 5, height: "6'4\"", weight: 225, ppg: 25.9, apg: 5.1, rpg: 5.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4594268.png' },
    { id: 'karl-anthony-towns', firstName: 'Karl-Anthony', lastName: 'Towns', position: 'C', number: 32, height: "6'11\"", weight: 248, ppg: 21.8, apg: 3.0, rpg: 8.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3136195.png' },
    { id: 'rudy-gobert', firstName: 'Rudy', lastName: 'Gobert', position: 'C', number: 27, height: "7'1\"", weight: 258, ppg: 14.0, apg: 1.3, rpg: 12.9, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3032976.png' },
    { id: 'jaden-mcdaniels', firstName: 'Jaden', lastName: 'McDaniels', position: 'SF', number: 3, height: "6'9\"", weight: 185, ppg: 15.2, apg: 2.9, rpg: 4.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4431671.png' },
    { id: 'mike-conley', firstName: 'Mike', lastName: 'Conley', position: 'PG', number: 10, height: "6'1\"", weight: 175, ppg: 9.4, apg: 5.9, rpg: 2.8, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3195.png' },
  ],
  'New Orleans Pelicans': [
    { id: 'zion-williamson', firstName: 'Zion', lastName: 'Williamson', position: 'PF', number: 1, height: "6'6\"", weight: 284, ppg: 22.9, apg: 5.0, rpg: 5.8, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4395628.png' },
    { id: 'brandon-ingram', firstName: 'Brandon', lastName: 'Ingram', position: 'SF', number: 14, height: "6'8\"", weight: 190, ppg: 20.8, apg: 5.7, rpg: 5.1, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3913176.png' },
    { id: 'cj-mccollum', firstName: 'CJ', lastName: 'McCollum', position: 'SG', number: 3, height: "6'3\"", weight: 190, ppg: 18.5, apg: 5.0, rpg: 4.1, image: 'https://a.espncdn.com/i/headshots/nba/players/full/2490149.png' },
    { id: 'jonas-valanciunas', firstName: 'Jonas', lastName: 'Valanciunas', position: 'C', number: 17, height: "6'11\"", weight: 265, ppg: 14.4, apg: 2.1, rpg: 8.8, image: 'https://a.espncdn.com/i/headshots/nba/players/full/6477.png' },
    { id: 'herbert-jones', firstName: 'Herbert', lastName: 'Jones', position: 'SF', number: 5, height: "6'8\"", weight: 210, ppg: 11.0, apg: 2.5, rpg: 3.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4277813.png' },
  ],
  'New York Knicks': [
    { id: 'jalen-brunson', firstName: 'Jalen', lastName: 'Brunson', position: 'PG', number: 11, height: "6'2\"", weight: 190, ppg: 28.7, apg: 6.7, rpg: 3.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3934672.png' },
    { id: 'julius-randle', firstName: 'Julius', lastName: 'Randle', position: 'PF', number: 30, height: "6'8\"", weight: 250, ppg: 24.0, apg: 5.0, rpg: 9.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3064514.png' },
    { id: 'rj-barrett', firstName: 'RJ', lastName: 'Barrett', position: 'SF', number: 9, height: "6'6\"", weight: 214, ppg: 19.6, apg: 2.6, rpg: 5.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4395625.png' },
    { id: 'mitchell-robinson', firstName: 'Mitchell', lastName: 'Robinson', position: 'C', number: 23, height: "7'0\"", weight: 240, ppg: 8.5, apg: 1.0, rpg: 8.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4351852.png' },
    { id: 'donte-divincenzo', firstName: 'Donte', lastName: 'DiVincenzo', position: 'SG', number: 0, height: "6'4\"", weight: 203, ppg: 15.5, apg: 3.5, rpg: 3.7, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3934673.png' },
  ],
  'Oklahoma City Thunder': [
    { id: 'shai-gilgeous-alexander', firstName: 'Shai', lastName: 'Gilgeous-Alexander', position: 'PG', number: 2, height: "6'6\"", weight: 195, ppg: 30.1, apg: 6.2, rpg: 5.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4278073.png' },
    { id: 'chet-holmgren', firstName: 'Chet', lastName: 'Holmgren', position: 'C', number: 7, height: "7'0\"", weight: 195, ppg: 16.5, apg: 2.4, rpg: 7.9, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4433255.png' },
    { id: 'jalen-williams', firstName: 'Jalen', lastName: 'Williams', position: 'SF', number: 8, height: "6'6\"", weight: 210, ppg: 19.1, apg: 4.5, rpg: 4.0, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4593803.png' },
    { id: 'josh-giddey', firstName: 'Josh', lastName: 'Giddey', position: 'SG', number: 3, height: "6'8\"", weight: 210, ppg: 12.3, apg: 6.4, rpg: 6.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4871145.png' },
    { id: 'lu-dort', firstName: 'Lu', lastName: 'Dort', position: 'SG', number: 5, height: "6'4\"", weight: 220, ppg: 10.8, apg: 1.9, rpg: 4.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4397020.png' },
  ],
  'Orlando Magic': [
    { id: 'paolo-banchero', firstName: 'Paolo', lastName: 'Banchero', position: 'PF', number: 5, height: "6'10\"", weight: 250, ppg: 22.6, apg: 5.4, rpg: 6.9, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4432573.png' },
    { id: 'franz-wagner', firstName: 'Franz', lastName: 'Wagner', position: 'SF', number: 22, height: "6'10\"", weight: 220, ppg: 19.7, apg: 3.7, rpg: 5.3, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4566434.png' },
    { id: 'jalen-suggs', firstName: 'Jalen', lastName: 'Suggs', position: 'PG', number: 4, height: "6'4\"", weight: 205, ppg: 12.0, apg: 4.4, rpg: 3.1, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4432165.png' },
    { id: 'wendell-carter', firstName: 'Wendell', lastName: 'Carter Jr.', position: 'C', number: 34, height: "6'10\"", weight: 270, ppg: 13.9, apg: 2.6, rpg: 8.8, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4277847.png' },
    { id: 'markelle-fultz', firstName: 'Markelle', lastName: 'Fultz', position: 'PG', number: 20, height: "6'3\"", weight: 209, ppg: 7.4, apg: 4.0, rpg: 2.8, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4066636.png' },
  ],
  'Philadelphia 76ers': [
    { id: 'joel-embiid', firstName: 'Joel', lastName: 'Embiid', position: 'C', number: 21, height: "7'0\"", weight: 280, ppg: 34.7, apg: 5.6, rpg: 11.0, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3059318.png' },
    { id: 'tyrese-maxey', firstName: 'Tyrese', lastName: 'Maxey', position: 'PG', number: 0, height: "6'2\"", weight: 200, ppg: 25.9, apg: 6.2, rpg: 3.7, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4431678.png' },
    { id: 'tobias-harris', firstName: 'Tobias', lastName: 'Harris', position: 'PF', number: 12, height: "6'8\"", weight: 226, ppg: 17.2, apg: 3.1, rpg: 6.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/6440.png' },
    { id: 'kyle-lowry', firstName: 'Kyle', lastName: 'Lowry', position: 'PG', number: 7, height: "6'0\"", weight: 196, ppg: 8.3, apg: 4.6, rpg: 3.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3012.png' },
    { id: 'kelly-oubre', firstName: 'Kelly', lastName: 'Oubre Jr.', position: 'SF', number: 9, height: "6'7\"", weight: 203, ppg: 15.4, apg: 1.2, rpg: 5.0, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3133603.png' },
  ],
  'Phoenix Suns': [
    { id: 'kevin-durant', firstName: 'Kevin', lastName: 'Durant', position: 'SF', number: 35, height: "6'10\"", weight: 240, ppg: 27.1, apg: 5.0, rpg: 6.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3202.png' },
    { id: 'devin-booker', firstName: 'Devin', lastName: 'Booker', position: 'SG', number: 1, height: "6'5\"", weight: 206, ppg: 27.1, apg: 6.9, rpg: 4.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3136193.png' },
    { id: 'bradley-beal', firstName: 'Bradley', lastName: 'Beal', position: 'SG', number: 3, height: "6'4\"", weight: 207, ppg: 18.2, apg: 5.0, rpg: 4.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/6580.png' },
    { id: 'jusuf-nurkic', firstName: 'Jusuf', lastName: 'Nurkic', position: 'C', number: 20, height: "6'11\"", weight: 290, ppg: 9.2, apg: 2.8, rpg: 9.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3102530.png' },
    { id: 'grayson-allen', firstName: 'Grayson', lastName: 'Allen', position: 'SG', number: 10, height: "6'4\"", weight: 198, ppg: 10.4, apg: 3.0, rpg: 3.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3135045.png' },
  ],
  'Portland Trail Blazers': [
    { id: 'anfernee-simons', firstName: 'Anfernee', lastName: 'Simons', position: 'SG', number: 1, height: "6'3\"", weight: 181, ppg: 22.6, apg: 5.5, rpg: 2.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4351851.png' },
    { id: 'scoot-henderson', firstName: 'Scoot', lastName: 'Henderson', position: 'PG', number: 0, height: "6'2\"", weight: 195, ppg: 14.0, apg: 5.4, rpg: 2.9, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4683678.png' },
    { id: 'jerami-grant', firstName: 'Jerami', lastName: 'Grant', position: 'PF', number: 9, height: "6'8\"", weight: 210, ppg: 20.1, apg: 2.8, rpg: 4.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/2991070.png' },
    { id: 'deandre-ayton', firstName: 'Deandre', lastName: 'Ayton', position: 'C', number: 2, height: "6'11\"", weight: 250, ppg: 16.7, apg: 1.6, rpg: 10.8, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4278129.png' },
    { id: 'shaedon-sharpe', firstName: 'Shaedon', lastName: 'Sharpe', position: 'SG', number: 17, height: "6'5\"", weight: 200, ppg: 15.9, apg: 2.4, rpg: 4.1, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4914336.png' },
  ],
  'Sacramento Kings': [
    { id: 'domantas-sabonis', firstName: 'Domantas', lastName: 'Sabonis', position: 'C', number: 10, height: "6'11\"", weight: 240, ppg: 19.4, apg: 8.2, rpg: 13.7, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3155942.png' },
    { id: 'deaaron-fox', firstName: "De'Aaron", lastName: 'Fox', position: 'PG', number: 5, height: "6'3\"", weight: 185, ppg: 26.6, apg: 5.6, rpg: 4.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4066259.png' },
    { id: 'malik-monk', firstName: 'Malik', lastName: 'Monk', position: 'SG', number: 0, height: "6'3\"", weight: 200, ppg: 13.9, apg: 4.5, rpg: 2.7, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4066262.png' },
    { id: 'keegan-murray', firstName: 'Keegan', lastName: 'Murray', position: 'SF', number: 13, height: "6'8\"", weight: 215, ppg: 14.1, apg: 2.0, rpg: 4.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4594327.png' },
    { id: 'harrison-barnes', firstName: 'Harrison', lastName: 'Barnes', position: 'PF', number: 40, height: "6'8\"", weight: 225, ppg: 12.2, apg: 2.4, rpg: 4.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/6578.png' },
  ],
  'San Antonio Spurs': [
    { id: 'victor-wembanyama', firstName: 'Victor', lastName: 'Wembanyama', position: 'C', number: 1, height: "7'4\"", weight: 210, ppg: 21.4, apg: 3.9, rpg: 10.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/5104157.png' },
    { id: 'devin-vassell', firstName: 'Devin', lastName: 'Vassell', position: 'SG', number: 24, height: "6'5\"", weight: 194, ppg: 19.5, apg: 4.1, rpg: 3.8, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4395630.png' },
    { id: 'keldon-johnson', firstName: 'Keldon', lastName: 'Johnson', position: 'SF', number: 3, height: "6'5\"", weight: 220, ppg: 15.7, apg: 2.5, rpg: 5.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4395723.png' },
    { id: 'jeremy-sochan', firstName: 'Jeremy', lastName: 'Sochan', position: 'PF', number: 10, height: "6'9\"", weight: 230, ppg: 11.3, apg: 3.8, rpg: 5.9, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4610139.png' },
    { id: 'tre-jones', firstName: 'Tre', lastName: 'Jones', position: 'PG', number: 33, height: "6'1\"", weight: 185, ppg: 10.8, apg: 6.3, rpg: 3.1, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4395626.png' },
  ],
  'Toronto Raptors': [
    { id: 'scottie-barnes', firstName: 'Scottie', lastName: 'Barnes', position: 'SF', number: 4, height: "6'7\"", weight: 225, ppg: 19.9, apg: 6.1, rpg: 8.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4433134.png' },
    { id: 'rj-barrett', firstName: 'RJ', lastName: 'Barrett', position: 'SF', number: 9, height: "6'6\"", weight: 214, ppg: 21.8, apg: 4.1, rpg: 6.4, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4395625.png' },
    { id: 'immanuel-quickley', firstName: 'Immanuel', lastName: 'Quickley', position: 'PG', number: 5, height: "6'3\"", weight: 188, ppg: 18.6, apg: 6.8, rpg: 4.8, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4395724.png' },
    { id: 'jakob-poeltl', firstName: 'Jakob', lastName: 'Poeltl', position: 'C', number: 19, height: "7'1\"", weight: 245, ppg: 11.1, apg: 2.4, rpg: 8.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3134908.png' },
    { id: 'dennis-schroder', firstName: 'Dennis', lastName: 'Schroder', position: 'PG', number: 17, height: "6'1\"", weight: 172, ppg: 13.5, apg: 5.0, rpg: 2.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3032979.png' },
  ],
  'Utah Jazz': [
    { id: 'lauri-markkanen', firstName: 'Lauri', lastName: 'Markkanen', position: 'PF', number: 23, height: "7'0\"", weight: 238, ppg: 25.6, apg: 1.9, rpg: 8.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4066336.png' },
    { id: 'jordan-clarkson', firstName: 'Jordan', lastName: 'Clarkson', position: 'SG', number: '00', height: "6'4\"", weight: 194, ppg: 20.1, apg: 4.4, rpg: 3.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/2528426.png' },
    { id: 'collin-sexton', firstName: 'Collin', lastName: 'Sexton', position: 'PG', number: 2, height: "6'1\"", weight: 190, ppg: 18.7, apg: 3.2, rpg: 2.7, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4277811.png' },
    { id: 'john-collins', firstName: 'John', lastName: 'Collins', position: 'PF', number: 20, height: "6'9\"", weight: 226, ppg: 16.2, apg: 2.3, rpg: 7.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3908845.png' },
    { id: 'walker-kessler', firstName: 'Walker', lastName: 'Kessler', position: 'C', number: 24, height: "7'1\"", weight: 245, ppg: 8.1, apg: 0.6, rpg: 7.5, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4433136.png' },
  ],
  'Washington Wizards': [
    { id: 'jordan-poole', firstName: 'Jordan', lastName: 'Poole', position: 'SG', number: 13, height: "6'4\"", weight: 194, ppg: 17.4, apg: 4.4, rpg: 2.7, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4277956.png' },
    { id: 'kyle-kuzma', firstName: 'Kyle', lastName: 'Kuzma', position: 'PF', number: 33, height: "6'10\"", weight: 221, ppg: 22.2, apg: 4.2, rpg: 6.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3134907.png' },
    { id: 'deni-avdija', firstName: 'Deni', lastName: 'Avdija', position: 'SF', number: 8, height: "6'9\"", weight: 210, ppg: 14.7, apg: 3.8, rpg: 7.2, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4683021.png' },
    { id: 'tyus-jones', firstName: 'Tyus', lastName: 'Jones', position: 'PG', number: 5, height: "6'0\"", weight: 196, ppg: 12.0, apg: 7.3, rpg: 2.8, image: 'https://a.espncdn.com/i/headshots/nba/players/full/3135046.png' },
    { id: 'marvin-bagley', firstName: 'Marvin', lastName: 'Bagley III', position: 'PF', number: 35, height: "6'11\"", weight: 235, ppg: 8.3, apg: 0.8, rpg: 5.6, image: 'https://a.espncdn.com/i/headshots/nba/players/full/4277848.png' },
  ],
};

// =============================================================================
// EUROLEAGUE TEAMS (2024-25 Season - Real API Data)
// =============================================================================

export const EUROLEAGUE_TEAMS = [
  { id: 'MAD', name: 'Real Madrid', country: 'Spain', imageUrl: 'https://media-cdn.incrowdsports.com/371b0d9b-9250-4c09-bda7-0686cf024657.png' },
  { id: 'BAR', name: 'FC Barcelona', country: 'Spain', imageUrl: 'https://media-cdn.incrowdsports.com/51b35809-3175-4b07-bf3e-93e3c1a09c2b.png' },
  { id: 'PAO', name: 'Panathinaikos AKTOR Athens', country: 'Greece', imageUrl: 'https://media-cdn.incrowdsports.com/2c69c2e9-f2bd-4a21-a04d-ad3800aedcb6.png' },
  { id: 'OLY', name: 'Olympiacos Piraeus', country: 'Greece', imageUrl: 'https://media-cdn.incrowdsports.com/2fbdbd8c-0ec4-4ac7-8f77-e28b11db78f2.png' },
  { id: 'IST', name: 'Fenerbahce Beko Istanbul', country: 'Turkey', imageUrl: 'https://media-cdn.incrowdsports.com/e8a6bbf0-ff8f-4a2a-a7fc-60d69b39f2da.png' },
  { id: 'ULK', name: 'Anadolu Efes Istanbul', country: 'Turkey', imageUrl: 'https://media-cdn.incrowdsports.com/a2c5bfb7-32ef-4dd5-80f7-b5ff24e8823d.png' },
  { id: 'TEL', name: 'Maccabi Playtika Tel Aviv', country: 'Israel', imageUrl: 'https://media-cdn.incrowdsports.com/18b00d5f-1fca-4ce7-a61d-53c2e2db8a2b.png' },
  { id: 'MIL', name: 'EA7 Emporio Armani Milan', country: 'Italy', imageUrl: 'https://media-cdn.incrowdsports.com/f5d31b0d-2e2e-4db1-9437-9a866a2eefbf.png' },
  { id: 'VIR', name: 'Virtus Segafredo Bologna', country: 'Italy', imageUrl: 'https://media-cdn.incrowdsports.com/1edb8d91-9f98-4fae-9a16-7e4d3e4e31a9.png' },
  { id: 'MUN', name: 'FC Bayern Munich', country: 'Germany', imageUrl: 'https://media-cdn.incrowdsports.com/b5d5a8f5-8e0e-4b05-9a0d-8d5cce5fa3a7.png' },
  { id: 'ASV', name: 'AS Monaco', country: 'France', imageUrl: 'https://media-cdn.incrowdsports.com/6e3c2e5e-8f0e-4b05-9a0d-8d5cce5fa3a7.png' },
  { id: 'PAR', name: 'Paris Basketball', country: 'France', imageUrl: 'https://media-cdn.incrowdsports.com/5d3c2e5e-8f0e-4b05-9a0d-8d5cce5fa3a7.png' },
  { id: 'RED', name: 'Crvena Zvezda Meridianbet Belgrade', country: 'Serbia', imageUrl: 'https://media-cdn.incrowdsports.com/4d3c2e5e-8f0e-4b05-9a0d-8d5cce5fa3a7.png' },
  { id: 'PAM', name: 'Partizan Mozzart Bet Belgrade', country: 'Serbia', imageUrl: 'https://media-cdn.incrowdsports.com/3d3c2e5e-8f0e-4b05-9a0d-8d5cce5fa3a7.png' },
  { id: 'ZAL', name: 'Zalgiris Kaunas', country: 'Lithuania', imageUrl: 'https://media-cdn.incrowdsports.com/2d3c2e5e-8f0e-4b05-9a0d-8d5cce5fa3a7.png' },
  { id: 'BAS', name: 'Baskonia Vitoria-Gasteiz', country: 'Spain', imageUrl: 'https://media-cdn.incrowdsports.com/1d3c2e5e-8f0e-4b05-9a0d-8d5cce5fa3a7.png' },
  { id: 'BER', name: 'ALBA Berlin', country: 'Germany', imageUrl: 'https://media-cdn.incrowdsports.com/0d3c2e5e-8f0e-4b05-9a0d-8d5cce5fa3a7.png' },
  { id: 'MCO', name: 'LDLC ASVEL Villeurbanne', country: 'France', imageUrl: 'https://media-cdn.incrowdsports.com/9d3c2e5e-8f0e-4b05-9a0d-8d5cce5fa3a7.png' },
];

// =============================================================================
// EUROLEAGUE TEAM METADATA (2025-26 Season)
// Coach info, standings, performance metrics for dashboard display
// =============================================================================

export const EUROLEAGUE_TEAM_METADATA = {
  'Real Madrid': {
    id: 'MAD',
    name: 'Real Madrid Baloncesto',
    abbreviation: 'RMB',
    coach: 'Sergio Scariolo',
    euroLeagueRecord: { wins: 13, losses: 8, position: 6 },
    ligaACBRecord: { wins: 13, losses: 2, position: 1 },
    netRating: 4.1,
    pointDifferential: 61,
    streakType: 'W',
    streakCount: 3,
    ppg: 85.3,
    oppPpg: 81.2,
    apg: 22.1,
    rpg: 35.4,
    fgPct: 0.478,
    threePct: 0.362,
    ftPct: 0.795,
    homeRecord: '9-2',
    awayRecord: '4-6',
    season: '2025-26',
  },
  'FC Barcelona': {
    id: 'BAR',
    name: 'FC Barcelona',
    abbreviation: 'BAR',
    coach: 'Joan Peñarroya',
    euroLeagueRecord: { wins: 15, losses: 6, position: 2 },
    ligaACBRecord: { wins: 12, losses: 3, position: 2 },
    netRating: 6.2,
    pointDifferential: 89,
    streakType: 'W',
    streakCount: 5,
    ppg: 88.7,
    oppPpg: 82.5,
    season: '2025-26',
  },
};

/**
 * Get team metadata for dashboard display
 * @param {string} teamName - Team name (e.g., 'Real Madrid')
 * @returns {Object|null} Team metadata object
 */
export function getEuroLeagueTeamMetadata(teamName) {
  const normalizedName = teamName?.toLowerCase().trim();
  const matchedTeam = Object.keys(EUROLEAGUE_TEAM_METADATA).find(key =>
    key.toLowerCase().includes(normalizedName) || normalizedName?.includes(key.toLowerCase())
  );
  return matchedTeam ? EUROLEAGUE_TEAM_METADATA[matchedTeam] : null;
}

// =============================================================================
// EUROLEAGUE ROSTERS (2025-26 Season - Updated with Local Images)
// =============================================================================

const EUROLEAGUE_ROSTERS = {
  'Real Madrid': [
    // 2025-26 Real Madrid Baloncesto roster - Updated January 2026
    // PIR leader, Playmaking Engine - NBA experience (Denver Nuggets)
    { id: '005928', firstName: 'Facundo', lastName: 'Campazzo', position: 'PG', number: 7, height: "5'10\"", weight: 175, age: 33, ppg: 12.2, apg: 6.4, rpg: 3.0, pir: 18.0, image: '/images/basketball/realmadrid/players/07_Campazzo.jpg', role: 'Primary ball handler', note: 'Elite hip dissociation, negative step braking' },
    // Top scorer, versatile wing, can play 2-4 positions
    { id: '003108', firstName: 'Mario', lastName: 'Hezonja', position: 'SF', number: 11, height: "6'8\"", weight: 220, age: 29, ppg: 14.6, apg: 1.6, rpg: 5.2, pir: 14.2, image: '/images/basketball/realmadrid/players/11_Hezonja.jpg', role: 'Primary offensive weapon', note: '27 points in El Clasico (Jan 4, 2026)' },
    // Rim Protector, "The Cape Verdean Wall" - EuroLeague premier shot-blocker
    { id: '005791', firstName: 'Walter', lastName: 'Tavares', position: 'C', number: 22, height: "7'3\"", weight: 273, age: 32, ppg: 10.6, apg: 1.1, rpg: 7.1, pir: 16.4, image: '/images/basketball/realmadrid/players/22_Tavares.jpg', role: 'Defensive anchor', note: '1.9 BPG - greatest rebounder in EuroLeague history' },
    // Marquee summer acquisition, leading PIR
    { id: '011001', firstName: 'Trey', lastName: 'Lyles', position: 'PF', number: 0, height: "6'10\"", weight: 234, age: 28, ppg: 15.9, apg: 1.8, rpg: 5.5, pir: 16.6, image: '/images/basketball/realmadrid/players/00_Lyles.jpg', role: 'Spacing 4, offensive catalyst', note: '21 pts vs Dubai (Jan 2, 2026)' },
    // Veteran presence, "The Tortuga"
    { id: '003667', firstName: 'Gabriel', lastName: 'Deck', position: 'PF', number: 14, height: "6'8\"", weight: 220, age: 29, ppg: 8.5, apg: 1.3, rpg: 3.2, pir: 9.0, image: '/images/basketball/realmadrid/players/14_Deck.jpg', role: 'Glue guy, versatile forward', note: 'Key in late-game situations' },
    // Legend Status - 19th season, 1,200+ career games (Captain)
    { id: '002195', firstName: 'Sergio', lastName: 'Llull', position: 'PG', number: 23, height: "6'3\"", weight: 200, age: 37, ppg: 6.7, apg: 2.1, rpg: 1.3, pir: 5.2, image: '/images/basketball/realmadrid/players/23_Llull.jpg', role: 'Veteran leadership, chaos agent', note: 'Big-game experience, clutch shooting' },
    // Defensive Swiss Army Knife - can switch 1-through-5
    { id: '009085', firstName: 'Usman', lastName: 'Garuba', position: 'PF', number: 16, height: "6'8\"", weight: 229, age: 22, ppg: 4.1, apg: 0.6, rpg: 3.3, pir: 4.4, image: '/images/basketball/realmadrid/players/16_Garuba.jpg', role: 'Defensive versatility', note: 'Critical for PnR coverages, returned from NBA' },
    // Solid defender, perimeter presence
    { id: '007318', firstName: 'Alberto', lastName: 'Abalde', position: 'SG', number: 6, height: "6'6\"", weight: 195, age: 29, ppg: 5.5, apg: 1.5, rpg: 2.3, pir: 5.9, image: '/images/basketball/realmadrid/players/06_Abalde.jpg', role: 'Wing defender', note: 'Struggles on screens vs elite guards' },
    // Young French point guard with upside
    { id: '012001', firstName: 'Theo', lastName: 'Maledon', position: 'PG', number: 12, height: "6'4\"", weight: 185, age: 23, ppg: 7.2, apg: 3.1, rpg: 2.0, pir: 8.5, image: '/images/basketball/realmadrid/players/12_Maledon.jpg', role: 'Backup point guard', note: 'Former NBA lottery pick, high motor' },
    // Backup guard, energy off bench
    { id: '009234', firstName: 'Andres', lastName: 'Feliz', position: 'PG', number: 24, height: "6'2\"", weight: 185, age: 27, ppg: 4.6, apg: 1.6, rpg: 1.7, pir: 4.3, image: '/images/basketball/realmadrid/players/24_Feliz.jpg', role: 'Depth guard', note: 'Provides energy, ball pressure' },
    // Former NBA center, veteran rim protection
    { id: '013001', firstName: 'Alex', lastName: 'Len', position: 'C', number: 25, height: "7'0\"", weight: 250, age: 31, ppg: 5.8, apg: 0.5, rpg: 4.2, pir: 7.1, image: '/images/basketball/realmadrid/players/25_Len.jpg', role: 'Backup center', note: 'Former NBA 5th pick, provides spacing' },
    // German sharpshooter
    { id: '014001', firstName: 'David', lastName: 'Kramer', position: 'SG', number: 1, height: "6'5\"", weight: 190, age: 25, ppg: 4.2, apg: 0.8, rpg: 1.5, pir: 4.0, image: '/images/basketball/realmadrid/players/01_Kramer.jpg', role: 'Shooter', note: 'Elite 3-point specialist' },
    // Athletic forward, Nigerian international
    { id: '015001', firstName: 'Chukwuma', lastName: 'Okeke', position: 'SF', number: 8, height: "6'8\"", weight: 220, age: 25, ppg: 3.5, apg: 0.6, rpg: 2.8, pir: 4.2, image: '/images/basketball/realmadrid/players/08_Okeke.jpg', role: 'Wing depth', note: 'Former Auburn Tiger, athletic finisher' },
    // Italian prospect
    { id: '016001', firstName: 'Gabriele', lastName: 'Procida', position: 'SF', number: 9, height: "6'7\"", weight: 200, age: 22, ppg: 3.0, apg: 0.5, rpg: 1.8, pir: 3.5, image: '/images/basketball/realmadrid/players/09_Procida.jpg', role: 'Developmental wing', note: '2022 NBA draft pick' },
    // Spanish youth prospect
    { id: '017001', firstName: 'Izan', lastName: 'Almansa', position: 'PF', number: 13, height: "6'9\"", weight: 210, age: 19, ppg: 2.0, apg: 0.3, rpg: 1.5, pir: 2.0, image: '/images/basketball/realmadrid/players/13_Almansa.jpg', role: 'Developmental forward', note: 'Top Spanish youth prospect' },
  ],
  'FC Barcelona': [
    // Barcelona roster - stats from EuroLeague 2024-25
    { id: '003512', firstName: 'Nikola', lastName: 'Mirotic', position: 'PF', number: 33, height: "6'10\"", weight: 250, age: 33, ppg: 14.8, apg: 1.9, rpg: 6.2, pir: 17.5, image: 'https://media-cdn.cortextech.io/player-mirotic.png' },
    { id: '006123', firstName: 'Nicolas', lastName: 'Laprovittola', position: 'PG', number: 20, height: "6'2\"", weight: 185, age: 34, ppg: 11.2, apg: 5.1, rpg: 2.4, pir: 13.8, image: 'https://media-cdn.cortextech.io/player-laprovittola.png' },
    { id: '004321', firstName: 'Willy', lastName: 'Hernangomez', position: 'C', number: 9, height: "6'11\"", weight: 240, age: 30, ppg: 10.8, apg: 1.3, rpg: 6.5, pir: 14.2, image: 'https://media-cdn.cortextech.io/player-hernangomez.png' },
    { id: '005678', firstName: 'Kevin', lastName: 'Punter', position: 'SG', number: 22, height: "6'1\"", weight: 175, age: 30, ppg: 12.5, apg: 2.8, rpg: 2.1, pir: 12.0, image: 'https://media-cdn.cortextech.io/player-punter.png' },
    { id: '007890', firstName: 'Jan', lastName: 'Vesely', position: 'PF', number: 24, height: "6'11\"", weight: 240, age: 33, ppg: 9.8, apg: 1.5, rpg: 4.8, pir: 11.5, image: 'https://media-cdn.cortextech.io/player-vesely.png' },
  ],
};

// =============================================================================
// WNBA TEAMS
// =============================================================================

// WNBA Teams (12 teams)
export const WNBA_TEAMS = [
  { id: 'ATL', name: 'Atlanta Dream', conference: 'Eastern' },
  { id: 'CHI', name: 'Chicago Sky', conference: 'Eastern' },
  { id: 'CON', name: 'Connecticut Sun', conference: 'Eastern' },
  { id: 'DAL', name: 'Dallas Wings', conference: 'Western' },
  { id: 'IND', name: 'Indiana Fever', conference: 'Eastern' },
  { id: 'LVA', name: 'Las Vegas Aces', conference: 'Western' },
  { id: 'LA', name: 'Los Angeles Sparks', conference: 'Western' },
  { id: 'MIN', name: 'Minnesota Lynx', conference: 'Western' },
  { id: 'NYL', name: 'New York Liberty', conference: 'Eastern' },
  { id: 'PHX', name: 'Phoenix Mercury', conference: 'Western' },
  { id: 'SEA', name: 'Seattle Storm', conference: 'Western' },
  { id: 'WAS', name: 'Washington Mystics', conference: 'Eastern' },
];

/**
 * Get teams for a specific basketball sport
 * @param {string} sportCode - NBA, WNBA, MBB, or WBB
 * @returns {Promise<Array>} Array of team objects
 */
export async function getBasketballTeams(sportCode) {
  const config = SPORT_CONFIG[sportCode];
  if (!config) {
    console.warn(`Unknown sport code: ${sportCode}`);
    return [];
  }

  // Return hardcoded teams for pro leagues
  // Include both 'name' and 'team_name' for component compatibility
  if (sportCode === 'NBA') {
    return NBA_TEAMS.map(team => ({
      id: team.id.toLowerCase(),
      team_id: team.id.toLowerCase(),
      name: team.name,  // Component expects 'name'
      team_name: team.name,
      full_name: team.name,
      conference: team.conference,
      division: team.division,
    }));
  }

  if (sportCode === 'WNBA') {
    return WNBA_TEAMS.map(team => ({
      id: team.id.toLowerCase(),
      team_id: team.id.toLowerCase(),
      name: team.name,  // Component expects 'name'
      team_name: team.name,
      full_name: team.name,
      conference: team.conference,
    }));
  }

  // Return EuroLeague teams
  if (sportCode === 'EURO') {
    return EUROLEAGUE_TEAMS.map(team => ({
      id: team.id.toLowerCase(),
      team_id: team.id.toLowerCase(),
      name: team.name,  // Component expects 'name'
      team_name: team.name,
      full_name: team.name,
      country: team.country,
      city: team.country,  // Use country as city for display
      imageUrl: team.imageUrl,
    }));
  }

  // Fetch from API for college
  try {
    const response = await apiClient.get(config.teamEndpoint, {
      params: { schema: sportCode },
    });
    return response?.data?.data || [];
  } catch (error) {
    console.error(`Error fetching ${sportCode} teams:`, error);
    return [];
  }
}

/**
 * Search players by team for a specific basketball sport
 * @param {string} sportCode - NBA, WNBA, MBB, or WBB
 * @param {string} teamName - Team name to search
 * @returns {Promise<Array>} Array of player objects
 */
export async function getBasketballPlayers(sportCode, teamName) {
  const config = SPORT_CONFIG[sportCode];
  if (!config || !teamName) {
    return [];
  }

  try {
    // For pro leagues, try ESPN API first
    if (sportCode === 'NBA' || sportCode === 'WNBA') {
      const espnTeam = await getESPNTeamRoster(sportCode, teamName);
      if (espnTeam && espnTeam.length > 0) {
        return espnTeam;
      }
    }

    // For EuroLeague, use local roster data
    if (sportCode === 'EURO') {
      const euroRoster = getEuroLeagueRoster(teamName);
      if (euroRoster && euroRoster.length > 0) {
        return euroRoster;
      }
    }

    // Fall back to SLAM API for college
    const response = await apiClient.post(
      config.playerEndpoint,
      { team_name: teamName },
      { params: { schema: sportCode } }
    );

    const players = response?.data?.data || [];

    // Deduplicate by player_name
    const uniquePlayers = Array.from(
      new Map(players.map(player => [player?.player_name, player])).values()
    );

    return uniquePlayers;
  } catch (error) {
    console.error(`Error fetching ${sportCode} players:`, error);
    return [];
  }
}

/**
 * Get player stats for a specific basketball sport
 * @param {string} sportCode - NBA, WNBA, MBB, or WBB
 * @param {string} playerId - Player ID
 * @returns {Promise<Object>} Player stats object
 */
export async function getBasketballPlayerStats(sportCode, playerId) {
  const config = SPORT_CONFIG[sportCode];
  if (!config || !playerId) {
    return null;
  }

  try {
    const response = await apiClient.get(`${config.statsEndpoint}/${playerId}`);
    return response?.data;
  } catch (error) {
    console.error(`Error fetching ${sportCode} player stats:`, error);
    return null;
  }
}

/**
 * Find matching team name in NBA_ROSTERS (case-insensitive, handles partial matches)
 * @param {string} teamName - Team name to search for
 * @returns {string|null} Matching team name key from NBA_ROSTERS
 */
function findMatchingTeam(teamName) {
  if (!teamName) return null;

  const normalizedSearch = teamName.toLowerCase().trim();

  // First try exact match (case-insensitive)
  const exactMatch = Object.keys(NBA_ROSTERS).find(
    key => key.toLowerCase() === normalizedSearch
  );
  if (exactMatch) return exactMatch;

  // Try matching by team nickname (last word, e.g., "Hawks" from "Atlanta Hawks")
  const searchNickname = normalizedSearch.split(' ').pop();
  const nicknameMatch = Object.keys(NBA_ROSTERS).find(key => {
    const keyNickname = key.toLowerCase().split(' ').pop();
    return keyNickname === searchNickname;
  });
  if (nicknameMatch) return nicknameMatch;

  // Try partial match (team name contains search or vice versa)
  const partialMatch = Object.keys(NBA_ROSTERS).find(key =>
    key.toLowerCase().includes(normalizedSearch) ||
    normalizedSearch.includes(key.toLowerCase())
  );

  return partialMatch || null;
}

/**
 * Get team roster from local data or ESPN API
 * @param {string} sportCode - NBA or WNBA
 * @param {string} teamName - Team name
 * @returns {Promise<Array>} Array of player objects
 */
async function getESPNTeamRoster(sportCode, teamName) {
  try {
    // Check for local NBA roster data first with flexible matching
    if (sportCode === 'NBA') {
      const matchedTeam = findMatchingTeam(teamName);
      if (matchedTeam && NBA_ROSTERS[matchedTeam]) {
        console.log(`Using local NBA roster for ${matchedTeam} (searched: "${teamName}")`);
        return NBA_ROSTERS[matchedTeam].map(player => ({
          // Include both id and player_id for component compatibility
          id: player.id,
          player_id: player.id,
          name: `${player.firstName} ${player.lastName}`,
          player_name: `${player.firstName} ${player.lastName}`,
          firstName: player.firstName,
          lastName: player.lastName,
          position: player.position,
          number: player.number,
          jersey_number: player.number,
          height: player.height,
          weight: player.weight,
          ppg: player.ppg,
          apg: player.apg,
          rpg: player.rpg,
          image: player.image,
          team: matchedTeam,
          // Include stats object for player profile page
          stats: {
            ppg: player.ppg,
            apg: player.apg,
            rpg: player.rpg,
          },
        }));
      }
    }

    const config = SPORT_CONFIG[sportCode];
    if (!config?.espnTeams) return [];

    // ESPN team lookup fallback
    console.log(`ESPN roster lookup for ${teamName} in ${sportCode} - no local data found`);
    return [];
  } catch (error) {
    console.error('ESPN roster fetch failed:', error);
    return [];
  }
}

/**
 * Get live scores from ESPN
 * @param {string} sportCode - NBA, WNBA, MBB, or WBB
 * @returns {Promise<Array>} Array of game objects
 */
export async function getLiveScores(sportCode) {
  const config = SPORT_CONFIG[sportCode];
  if (!config?.espnScoreboard) {
    return [];
  }

  try {
    const response = await fetch(config.espnScoreboard);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();

    return data.events?.map(event => ({
      id: event.id,
      name: event.name,
      status: event.status?.type?.description,
      homeTeam: event.competitions?.[0]?.competitors?.find(c => c.homeAway === 'home'),
      awayTeam: event.competitions?.[0]?.competitors?.find(c => c.homeAway === 'away'),
      startTime: event.date,
    })) || [];
  } catch (error) {
    console.error(`Error fetching ${sportCode} scores:`, error);
    return [];
  }
}

/**
 * Get basketball-specific metrics for a player
 * For use in analytics dashboards
 * @param {string} sportCode - NBA, WNBA, MBB, or WBB
 * @param {string} playerId - Player ID
 * @returns {Promise<Object>} Basketball metrics
 */
export async function getBasketballMetrics(sportCode, playerId) {
  // Standard basketball metrics structure
  const defaultMetrics = {
    points: 0,
    rebounds: 0,
    assists: 0,
    steals: 0,
    blocks: 0,
    turnovers: 0,
    fieldGoalPct: 0,
    threePointPct: 0,
    freeThrowPct: 0,
    plusMinus: 0,
    per: 0, // Player Efficiency Rating
    usageRate: 0,
    trueShootingPct: 0,
  };

  try {
    const stats = await getBasketballPlayerStats(sportCode, playerId);
    if (!stats) return defaultMetrics;

    return {
      ...defaultMetrics,
      ...stats,
    };
  } catch (error) {
    console.error(`Error fetching basketball metrics:`, error);
    return defaultMetrics;
  }
}

/**
 * Check if a sport code is a basketball sport
 * @param {string} sportCode - Sport code to check
 * @returns {boolean}
 */
export function isBasketballSport(sportCode) {
  return ['NBA', 'WNBA', 'MBB', 'WBB', 'EURO'].includes(sportCode);
}

/**
 * Check if a sport code is a pro basketball league
 * @param {string} sportCode - Sport code to check
 * @returns {boolean}
 */
export function isProBasketball(sportCode) {
  return ['NBA', 'WNBA'].includes(sportCode);
}

/**
 * Check if a sport code is a college basketball league
 * @param {string} sportCode - Sport code to check
 * @returns {boolean}
 */
export function isCollegeBasketball(sportCode) {
  return ['MBB', 'WBB'].includes(sportCode);
}

/**
 * Check if a sport code is EuroLeague
 * @param {string} sportCode - Sport code to check
 * @returns {boolean}
 */
export function isEuroLeague(sportCode) {
  return sportCode === 'EURO';
}

/**
 * Get a specific player by ID from any basketball league
 * @param {string} sportCode - NBA, WNBA, MBB, WBB, or EURO
 * @param {string} teamName - Team name to search
 * @param {string} playerId - Player ID to find
 * @returns {Promise<Object|null>} Player object or null if not found
 */
export async function getBasketballPlayerById(sportCode, teamName, playerId) {
  if (!playerId) return null;

  const players = await getBasketballPlayers(sportCode, teamName);
  if (!players || players.length === 0) return null;

  // Normalize playerId for comparison (handle different ID formats)
  const normalizedPlayerId = String(playerId).toLowerCase().trim();

  // Find player by various ID fields (handles different data source formats)
  const player = players.find(p => {
    const pId = String(p.id || '').toLowerCase().trim();
    const pPlayerId = String(p.player_id || '').toLowerCase().trim();
    const pPlayerIdCamel = String(p.playerId || '').toLowerCase().trim();
    const pName = (p.name || p.player_name || '').toLowerCase().replace(/\s+/g, '-');

    return pId === normalizedPlayerId ||
           pPlayerId === normalizedPlayerId ||
           pPlayerIdCamel === normalizedPlayerId ||
           pName === normalizedPlayerId;
  });

  if (!player) return null;

  // Ensure player has all needed fields with consistent format
  return {
    ...player,
    id: player.id || player.player_id,
    name: player.name || player.player_name || `${player.firstName} ${player.lastName}`,
    team: player.team || teamName,
    league: sportCode,
    stats: player.stats || {
      ppg: player.ppg || 0,
      apg: player.apg || 0,
      rpg: player.rpg || 0,
      pir: player.pir || null,
    },
  };
}

/**
 * Get EuroLeague roster for a team
 * @param {string} teamName - Team name to search
 * @returns {Array} Array of player objects
 */
export function getEuroLeagueRoster(teamName) {
  if (!teamName) return [];

  const normalizedSearch = teamName.toLowerCase().trim();

  // Find matching team in EUROLEAGUE_ROSTERS
  const matchedTeam = Object.keys(EUROLEAGUE_ROSTERS).find(key => {
    const keyLower = key.toLowerCase();
    return keyLower === normalizedSearch ||
           keyLower.includes(normalizedSearch) ||
           normalizedSearch.includes(keyLower);
  });

  if (!matchedTeam) return [];

  return EUROLEAGUE_ROSTERS[matchedTeam].map(player => ({
    // IDs
    id: player.id,
    player_id: player.id,
    // Names - support multiple formats used across the app
    name: `${player.firstName} ${player.lastName}`,
    player_name: `${player.firstName} ${player.lastName}`,
    firstName: player.firstName,
    lastName: player.lastName,
    // Player info
    position: player.position,
    number: player.number,
    jersey_number: player.number,
    height: player.height,
    weight: player.weight ? `${player.weight} lbs` : null,
    age: player.age,
    // Stats - both flat and nested format for different components
    ppg: player.ppg,
    apg: player.apg,
    rpg: player.rpg,
    pir: player.pir,
    stats: {
      ppg: player.ppg,
      apg: player.apg,
      rpg: player.rpg,
      pir: player.pir, // EuroLeague Performance Index Rating
    },
    image: player.image,
    team: matchedTeam,
  }));
}

/**
 * Get all EuroLeague teams
 * @returns {Array} Array of team objects
 */
export function getEuroLeagueTeams() {
  return EUROLEAGUE_TEAMS;
}

export default {
  getBasketballTeams,
  getBasketballPlayers,
  getBasketballPlayerStats,
  getBasketballMetrics,
  getLiveScores,
  isBasketballSport,
  isProBasketball,
  isCollegeBasketball,
  isEuroLeague,
  getEuroLeagueRoster,
  getEuroLeagueTeams,
  NBA_TEAMS,
  WNBA_TEAMS,
  EUROLEAGUE_TEAMS,
  SPORT_CONFIG,
};
